import { z } from 'zod';

/**
 * Facilitation mode — the Campaign-level posture that shapes who can ratify
 * drafts, who can move the session cursor, and how member writes reach canon
 * (`07-session-experience.md` §2.2). Four declarative modes:
 *
 * - `gm`       — one User with Campaign role = `gm`; standard hierarchy. The GM
 *                is the sole ratifier; member writes route through draft/pending.
 * - `solo`     — one User playing alone (as their own GM). The lone User may
 *                write canon directly by default; the draft pipeline stays
 *                available opt-in.
 * - `rotating` — multiple Users; the per-session `session_facilitator` ratifies
 *                (Phase 2 surface). Non-facilitator member writes route through
 *                draft/pending.
 * - `flat`     — multiple Users; no hierarchical ratification — every member
 *                write auto-canons directly (`authored_by: 'player'`, no
 *                ratification). The LLM still routes through draft/pending.
 *
 * `gm` and `solo` ship Phase 1; `rotating` and `flat` ship Phase 2 alongside the
 * multi-user / member surfaces — but the *policy* for all four ships now (this
 * module), so the Phase-2 UIs are thin.
 *
 * This is a code-typed, system-closed enum (ADR-0004 §2), authoritative in the
 * "setting" bucket as the `facilitation_mode` config key (`config-ops.ts`).
 */
export const FacilitationModeSchema = z.enum(['gm', 'solo', 'rotating', 'flat']);
export type FacilitationMode = z.infer<typeof FacilitationModeSchema>;

/** The default mode for a freshly-created Campaign (`07 §2.2`: `gm` is default). */
export const DEFAULT_FACILITATION_MODE: FacilitationMode = 'gm';

/**
 * The authoring role the facilitation policy keys on. NOT the same as a fact
 * author's `kind` — it is the *posture-relative* role of the writer:
 *
 * - `lone_user`   — the sole User in a `solo` campaign (their own GM).
 * - `gm`          — the User holding the canon-source role (`gm` mode) OR the
 *                   current `session_facilitator` (`rotating` mode). The "GM is
 *                   canon source" invariant is mode-relative (`07 §2.2`).
 * - `member`      — a non-facilitator member (a player; or any co-author in a
 *                   `flat` campaign).
 * - `llm`         — the drafting copilot. NEVER auto-canons, in ANY mode — the
 *                   "LLM never writes canon" invariant is mode-INDEPENDENT
 *                   (`00-vision.md`; `07 §2.2`). Mapped here only so the resolver
 *                   is total; the DO's `assertLlmAuthoringInvariant` is the
 *                   orthogonal, authoritative LLM guard (this resolver must never
 *                   become an LLM auto-canon path).
 * - `system`      — engine-authored ops (e.g. the #223 pending-lane promotion).
 *                   Not a member write; never auto-canon-eligible here.
 */
export const FacilitationAuthorRoleSchema = z.enum([
  'lone_user',
  'gm',
  'member',
  'llm',
  'system',
]);
export type FacilitationAuthorRole = z.infer<typeof FacilitationAuthorRoleSchema>;

/**
 * The resolved authority a `(mode, authorRole)` pair carries — the pure
 * chokepoint the DO append gate enforces (`07 §2.2`). Three orthogonal
 * capabilities:
 *
 * - `canRatify`        — may this role ratify drafts → canon (the explicit
 *                        per-fact GM gesture)? GM/facilitator/lone-user yes;
 *                        plain members no (except none in `flat` — there is no
 *                        ratification step at all there).
 * - `canMoveCursor`    — may this role move the session cursor (the in-world
 *                        "now")? Mirrors the ratifier in `gm`/`solo`/`rotating`;
 *                        any active member in `flat`.
 * - `autoCanonEligible`— does a fresh member/human write by this role land
 *                        directly in canon WITHOUT a ratification step? True only
 *                        for: the lone User in `solo`, any member in `flat`, and
 *                        the canon-source role (gm/facilitator) writing their own
 *                        canon. NEVER true for `llm` (mode-independent) or
 *                        `system`.
 */
export interface FacilitationPolicy {
  canRatify: boolean;
  canMoveCursor: boolean;
  autoCanonEligible: boolean;
}

/**
 * Resolve the facilitation policy for a `(mode, authorRole)` pair. Pure +
 * total + side-effect-free — the single chokepoint resolver (`07 §2.2`). Each
 * mode is a declarative config of this one function; there is NO per-mode
 * special-casing branch in the caller.
 *
 * Hard rules baked in:
 *  - **The LLM never auto-canons, in any mode.** `authorRole === 'llm'` always
 *    yields `autoCanonEligible: false` (and no ratify / cursor authority). This
 *    is mode-INDEPENDENT and orthogonal to the DO's `assertLlmAuthoringInvariant`
 *    (which is the authoritative LLM guard); the resolver simply must never open
 *    an LLM auto-canon path.
 *  - **`system` ops are not member writes.** They are never auto-canon-eligible
 *    here (the #223 pending-lane promotion rides its own provenance, not this
 *    policy).
 *  - **Canon authority is mode-relative.** `gm` role = the User with the
 *    canon-source role in `gm`, or the current facilitator in `rotating`. The
 *    `lone_user` role only carries authority in `solo`.
 */
export function resolveFacilitationPolicy(
  mode: FacilitationMode,
  authorRole: FacilitationAuthorRole,
): FacilitationPolicy {
  const denied: FacilitationPolicy = {
    canRatify: false,
    canMoveCursor: false,
    autoCanonEligible: false,
  };

  // LLM + system never carry author-time canon authority, regardless of mode.
  // The LLM rule is the non-negotiable, mode-independent invariant.
  if (authorRole === 'llm' || authorRole === 'system') return denied;

  switch (mode) {
    case 'solo':
      // The lone User is their own GM: full authority + direct-to-canon by
      // default (the draft pipeline remains available opt-in — that is a write
      // choice, not a policy denial).
      return authorRole === 'lone_user'
        ? { canRatify: true, canMoveCursor: true, autoCanonEligible: true }
        : denied;

    case 'gm':
      // Standard hierarchy: the GM ratifies + moves the cursor + writes their
      // own canon directly. A plain member proposes via drafts (no auto-canon).
      return authorRole === 'gm'
        ? { canRatify: true, canMoveCursor: true, autoCanonEligible: true }
        : denied;

    case 'rotating':
      // The current session's facilitator (mapped to the `gm` role by the
      // caller) is the sole ratifier + cursor authority; non-facilitator members
      // route through drafts.
      return authorRole === 'gm'
        ? { canRatify: true, canMoveCursor: true, autoCanonEligible: true }
        : denied;

    case 'flat':
      // No hierarchical ratification: every member write auto-canons directly,
      // any active member may move the cursor, and there is no ratification step
      // (`canRatify: false` for all — nothing to ratify). The canon-source `gm`
      // role does not exist in flat; a `gm`-tagged caller is treated as a member.
      return { canRatify: false, canMoveCursor: true, autoCanonEligible: true };
  }
}

/**
 * Auto-suggest a facilitation mode from a Campaign's membership shape at
 * creation time (`07 §2.2` "Auto-detection"). A SUGGESTION only — the user may
 * override it. Deterministic:
 *
 *  - exactly one member            → `solo`
 *  - no member holds Campaign role `gm` (and >1 member) → `flat`
 *  - otherwise                     → `gm` (the default)
 *
 * `memberCount` is the total member count; `hasGmRole` is whether any member
 * holds the `gm` Campaign role (the implicit-GM owner counts as a GM-roled
 * member).
 */
export function suggestFacilitationMode(input: {
  memberCount: number;
  hasGmRole: boolean;
}): FacilitationMode {
  if (input.memberCount <= 1) return 'solo';
  if (!input.hasGmRole) return 'flat';
  return 'gm';
}

/**
 * Does the chosen mode CONTRADICT the membership shape? The deterministic
 * `facilitation.mode_mismatch` auditor advisory (info severity, `07 §2.2`)
 * fires when this returns a reason. Returns `null` when the mode is consistent
 * with membership, or a stable reason code when it contradicts:
 *
 *  - `solo_multi_member` — `solo` mode but more than one member.
 *  - `gm_no_gm_role`     — `gm` (or `rotating`) mode but no member holds the
 *                          `gm` role (no canon source exists).
 *
 * `flat` never mismatches (it makes no membership demand). A single-member
 * `gm`/`rotating` campaign is NOT flagged (a solo user keeping the GM framing
 * for future invitees is explicitly supported — `07 §2.1`).
 */
export type FacilitationMismatchReason = 'solo_multi_member' | 'gm_no_gm_role';

export function facilitationModeMismatch(input: {
  mode: FacilitationMode;
  memberCount: number;
  hasGmRole: boolean;
}): FacilitationMismatchReason | null {
  switch (input.mode) {
    case 'solo':
      return input.memberCount > 1 ? 'solo_multi_member' : null;
    case 'gm':
    case 'rotating':
      return input.hasGmRole ? null : 'gm_no_gm_role';
    case 'flat':
      return null;
  }
}
